/**
 * Horizontal chip strip of King + top Gatekeeper per expiration.
 * Tag legend:
 *   above spot + long gamma  → SUPPORT (gold — positive gamma)
 *   below spot + long gamma  → MAGNET  (gold — positive gamma)
 *   above spot + short gamma → CAP     (red  — negative gamma)
 *   below spot + short gamma → DANGER  (red  — negative gamma)
 */

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { HeatmapFrame, Node } from '../../lib/types';
import { STORAGE_KEYS } from '../../lib/constants';
import { formatStrike } from '../../lib/format';

// Persist the collapsed state across sessions. Single key — applies to
// every page that mounts the strip (today, just /app/options).
const COLLAPSED_KEY = STORAGE_KEYS.starnodesCollapsed;

function loadCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage.getItem(COLLAPSED_KEY) === '1'; }
  catch { return false; }
}

interface StarNodesStripProps {
  frame: HeatmapFrame | null;
}

interface Chip {
  expiration: string;
  strike: number;
  isLong: boolean;
  tag: 'SUPPORT' | 'MAGNET' | 'CAP' | 'DANGER';
  emoji: string;
  rank: 'KING' | 'GK';
}

function tagFor(strike: number, spot: number, isPositive: boolean): Chip['tag'] {
  if (isPositive) return strike >= spot ? 'SUPPORT' : 'MAGNET';
  return strike >= spot ? 'CAP' : 'DANGER';
}

function classifyChips(frame: HeatmapFrame): Chip[] {
  const spot = frame.spot_price;
  const out: Chip[] = [];

  // Iterate per-expiration so we get one chip per expiry's leading node.
  const exps = Object.keys(frame.gex_by_expiration || {}).sort();
  for (const exp of exps) {
    const data = frame.gex_by_expiration[exp] || {};
    let topStrike: number | null = null;
    let topAbs = 0;
    let topVal = 0;
    for (const [k, v] of Object.entries(data)) {
      const s = parseFloat(k);
      const av = Math.abs(v);
      if (!isNaN(s) && av > topAbs) {
        topAbs = av;
        topStrike = s;
        topVal = v;
      }
    }
    if (topStrike == null) continue;
    const isLong = topVal >= 0;
    out.push({
      expiration: exp,
      strike: topStrike,
      isLong,
      tag: tagFor(topStrike, spot, isLong),
      emoji: '★',
      rank: 'KING',
    });
  }
  return out;
}

function formatExp(exp: string): string {
  // ISO like 2026-05-17 → MAY-17
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(exp);
  if (!m) return exp;
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  return `${months[parseInt(m[2], 10) - 1]}-${m[3]}`;
}

const StarNodesStrip: React.FC<StarNodesStripProps> = ({ frame }) => {
  const chips = useMemo<Chip[]>(() => (frame ? classifyChips(frame) : []), [frame]);
  const [collapsed, setCollapsed] = useState<boolean>(() => loadCollapsed());

  useEffect(() => {
    try { window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0'); }
    catch { /* private mode / quota — non-fatal */ }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed(c => !c), []);

  return (
    <section
      className={`sn-wrap${collapsed ? ' sn-wrap--collapsed' : ''}`}
      aria-label="Star nodes per expiration"
    >
      <div className="sn-strip">
        <button
          type="button"
          className="sn-header sn-toggle"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-controls="sn-content"
          title={collapsed ? 'Expand star nodes' : 'Collapse star nodes'}
        >
          <span className="sn-caret" aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
          STAR NODES
          {collapsed && chips.length > 0 && (
            <span className="sn-count" aria-hidden="true">· {chips.length}</span>
          )}
        </button>
        {!collapsed && (
          <>
            {chips.length === 0 && <span className="sn-empty">No nodes for this frame yet.</span>}
            {chips.map(c => (
              <span key={c.expiration} className={`sn-chip ${c.isLong ? 'long' : 'short'}`}>
                <span className="sn-exp">[{formatExp(c.expiration)}]</span>
                <span className="sn-strike">${formatStrike(c.strike)}</span>
                <span className="sn-tag">{c.tag}</span>
              </span>
            ))}
          </>
        )}
      </div>
      {!collapsed && (
        <p className="sn-legend" id="sn-content">
          Strongest dealer wall per expiry. <span className="sn-cyan">Gold</span> tags are long gamma (pins price):
          <strong> support</strong> above spot caps rallies, <strong>magnet</strong> below spot cushions drops.
          <span className="sn-red"> Red</span> tags are short gamma (amplifies moves):
          <strong> cap</strong> above spot triggers breakouts, <strong>danger</strong> below spot triggers cascades.
        </p>
      )}
    </section>
  );
};

export default StarNodesStrip;
